import { useQuery } from '@tanstack/react-query';
import type { SubjectListItem } from '@k-management/shared';
import { useNavigate } from 'react-router';
import { useCan, useCurrentUser } from '../../auth/AuthProvider.js';
import { DataGrid, type GridColumn } from '../../components/DataGrid.js';
import { type Command, useCommands } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { downloadText } from '../tables/table-format.js';
import {
  lookupLabel,
  registryKeys,
  RoleBadge,
  StatePill,
  useRegistryLookups,
} from './registry-ui.js';
import { useListFilters } from '../../lib/list-filters.js';

type StateFilter = 'active' | 'all' | 'inactive' | 'incomplete';

/** Anagrafica: every subject the user may see, one row each whatever its roles (proposal §2.5). */
export function RegistryListPage() {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const canWrite = useCan('anagrafica.write');
  const canExport = useCan('anagrafica.export');
  const canAdmin = useCan('anagrafica.admin');
  const lookups = useRegistryLookups();
  const subjects = useQuery({
    queryKey: registryKeys.all,
    queryFn: () => apiRequest<SubjectListItem[]>('/subjects'),
  });
  const pending = useQuery({
    queryKey: [...registryKeys.requests, 'count'],
    queryFn: () => apiRequest<{ count: number }>('/unlock-requests/pending-count'),
    enabled: canAdmin,
  });
  // The filters stay while the browser is open: found again coming back to the list (1.61).
  const { filters, setFilter, reset, changed, changedClass } = useListFilters('registry', {
    role: 'all' as string,
    state: 'active' as StateFilter,
  });
  const { role, state } = filters;
  const setRole = (value: string) => setFilter('role', value);
  const setState = (value: StateFilter) => setFilter('state', value);
  const own = user.scopes.anagrafica === 'own';

  const all = subjects.data ?? [];
  const rows = all.filter(
    (subject) =>
      (role === 'all' || subject.roles.some((item) => item.roleCode === role)) &&
      (state === 'all' ||
        (state === 'active' && subject.active) ||
        (state === 'inactive' && !subject.active) ||
        (state === 'incomplete' && !subject.complete)),
  );
  const presentRoles = (lookups.data?.roles ?? []).filter((item) =>
    all.some((subject) => subject.roles.some((subjectRole) => subjectRole.roleCode === item.code)),
  );

  const columns: GridColumn<SubjectListItem>[] = [
    {
      key: 'codes',
      label: 'Codici',
      // The historic codes of the Tesoreria are searched too (C0001, F0001).
      value: (subject) =>
        subject.roles.flatMap((item) => [item.code, item.legacyCode ?? '']).join(' '),
      render: (subject) => (
        <span className="code">{subject.roles.map((item) => item.code).join(' · ')}</span>
      ),
    },
    {
      key: 'name',
      label: 'Ragione sociale',
      required: true,
      value: (subject) => `${subject.name} ${subject.aliases.join(' ')}`,
      render: (subject) => (
        <>
          <button
            type="button"
            className="link strong"
            onClick={() => navigate(`/anagrafica/${subject.id}`)}
          >
            {subject.name}
          </button>
          {subject.aliases[0] && (
            <small className="muted block">anche «{subject.aliases[0]}»</small>
          )}
        </>
      ),
    },
    {
      key: 'roles',
      label: 'Ruoli',
      render: (subject) => (
        <span className="flags">
          {subject.roles.map((item) => (
            <RoleBadge key={item.code} role={item} />
          ))}
        </span>
      ),
    },
    ...(lookups.data?.classifications ?? []).map((table): GridColumn<SubjectListItem> => ({
      key: `class.${table.key}`,
      label: table.name,
      value: (subject) => lookupLabel(table.entries, subject.classifications[table.key]),
      render: (subject) => lookupLabel(table.entries, subject.classifications[table.key]),
    })),
    {
      key: 'city',
      label: 'Comune',
      value: (subject) => subject.city ?? '',
      render: (subject) => subject.city,
    },
    {
      key: 'province',
      label: 'Prov.',
      value: (subject) => subject.province ?? '',
      render: (subject) => subject.province,
    },
    {
      key: 'vat',
      label: 'P.IVA',
      value: (subject) => subject.vatNumber ?? '',
      render: (subject) => <span className="code">{subject.vatNumber}</span>,
    },
    {
      key: 'taxCode',
      label: 'Codice fiscale',
      hiddenByDefault: true,
      value: (subject) => subject.taxCode ?? '',
      render: (subject) => <span className="code">{subject.taxCode}</span>,
    },
    {
      key: 'phone',
      label: 'Telefono',
      hiddenByDefault: true,
      value: (s) => s.phone ?? '',
      render: (s) => s.phone,
    },
    {
      key: 'email',
      label: 'E-mail',
      hiddenByDefault: true,
      value: (s) => s.email ?? '',
      render: (s) => s.email,
    },
    {
      key: 'type',
      label: 'Tipologia',
      hiddenByDefault: true,
      value: (subject) => lookupLabel(lookups.data?.types, subject.typeCode),
      render: (subject) => lookupLabel(lookups.data?.types, subject.typeCode),
    },
    ...(own
      ? []
      : [
          {
            key: 'owner',
            label: 'In carico a',
            value: (subject: SubjectListItem) => subject.owner?.name ?? '',
            render: (subject: SubjectListItem) => (
              <>
                {subject.owner?.name ?? '—'}
                {subject.sharedWith.length > 0 && (
                  <small className="muted block">
                    sbloccato per {subject.sharedWith.map((share) => share.name).join(', ')}
                  </small>
                )}
              </>
            ),
          },
        ]),
    {
      key: 'state',
      label: 'Stato',
      value: (subject) =>
        subject.complete ? (subject.active ? 'Attivo' : 'Disattivato') : 'Da completare',
      render: (subject) => <StatePill subject={subject} />,
    },
  ];

  const commands: (Command | false)[] = [
    canWrite && {
      key: 'new',
      label: 'Nuovo',
      icon: 'plus',
      primary: true,
      onClick: () => navigate('/anagrafica/nuovo'),
    },
    canExport && {
      key: 'export',
      label: 'Esporta CSV',
      icon: 'down',
      disabled: rows.length === 0,
      onClick: () => downloadText('anagrafica.csv', toCsv(rows, lookups.data?.types)),
    },
    canAdmin && {
      key: 'import',
      label: 'Importa',
      icon: 'upload' as const,
      onClick: () => navigate('/anagrafica/importa'),
    },
    canAdmin && {
      key: 'requests',
      label: pending.data?.count ? `Richieste (${pending.data.count})` : 'Richieste di sblocco',
      icon: 'shield',
      onClick: () => navigate('/anagrafica/richieste'),
    },
  ];
  useCommands(commands.filter((command): command is Command => Boolean(command)));

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Anagrafica</h1>
          <p className="muted">
            Soggetti condivisi da tutti i moduli: un soggetto, uno o più ruoli.
          </p>
        </div>
      </div>
      {own && (
        <p className="notice info">
          Vedi le anagrafiche inserite da te o sbloccate per te dall’amministratore.
        </p>
      )}
      {canAdmin && Boolean(pending.data?.count) && (
        <div className="notice">
          <strong>
            {pending.data!.count === 1
              ? '1 richiesta di sblocco'
              : `${pending.data!.count} richieste di sblocco`}{' '}
            in attesa
          </strong>
          : qualcuno ha provato a inserire un’anagrafica già presente in archivio.
          <button
            type="button"
            className="small primary"
            onClick={() => navigate('/anagrafica/richieste')}
          >
            Rivedi
          </button>
        </div>
      )}
      {subjects.isPending && <p className="muted">Caricamento…</p>}
      {subjects.isError && <p className="error">{subjects.error.message}</p>}
      {subjects.data && (
        <DataGrid
          id="registry"
          filtersChanged={changed}
          onResetFilters={reset}
          columns={columns}
          rows={rows}
          rowKey={(subject) => subject.id}
          searchPlaceholder="Nome, alias, P.IVA, codice fiscale, codice"
          empty={
            canWrite
              ? 'Nessun soggetto: aggiungi il primo con «Nuovo».'
              : 'Nessun soggetto visibile con il tuo profilo.'
          }
          rowClassName={(subject) => (subject.active ? undefined : 'inactive')}
          onRowOpen={(subject) => navigate(`/anagrafica/${subject.id}`)}
          toolbar={
            <>
              <div className="chips" role="group" aria-label="Ruolo">
                <button
                  type="button"
                  className={role === 'all' ? 'chip selected' : 'chip'}
                  onClick={() => setRole('all')}
                >
                  Tutti
                </button>
                {presentRoles.map((item) => (
                  <button
                    key={item.code}
                    type="button"
                    className={role === item.code ? 'chip selected' : 'chip'}
                    onClick={() => setRole(item.code)}
                  >
                    {item.description}
                  </button>
                ))}
              </div>
              <select
                className={changedClass('state')}
                aria-label="Stato"
                value={state}
                onChange={(e) => setState(e.target.value as StateFilter)}
              >
                <option value="active">Attivi</option>
                <option value="all">Tutti gli stati</option>
                <option value="inactive">Disattivati</option>
                <option value="incomplete">Da completare</option>
              </select>
            </>
          }
        />
      )}
    </section>
  );
}

function toCsv(rows: SubjectListItem[], types?: { code: string; description: string }[]): string {
  const header = [
    'Codici',
    'Ragione sociale',
    'Ruoli',
    'Tipologia',
    'P.IVA',
    'Codice fiscale',
    'Comune',
    'Provincia',
    'Paese',
    'Telefono',
    'E-mail',
    'Stato',
  ];
  const lines = rows.map((subject) => [
    subject.roles.map((role) => role.code).join(' '),
    subject.name,
    subject.roles.map((role) => role.roleName).join(', '),
    lookupLabel(types, subject.typeCode),
    subject.vatNumber ?? '',
    subject.taxCode ?? '',
    subject.city ?? '',
    subject.province ?? '',
    subject.country,
    subject.phone ?? '',
    subject.email ?? '',
    subject.complete ? (subject.active ? 'Attivo' : 'Disattivato') : 'Da completare',
  ]);
  return [header, ...lines]
    .map((line) =>
      line.map((cell) => (/[";\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell)).join(';'),
    )
    .join('\r\n');
}
